import { useEffect, useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import dataService from 'services/dataService';
import {
  apiPatientChartSessionEnd,
  apiPatientChartSessionStart,
  apiPatientChartSessionPing,
} from 'services/dataService/resources';

import { showResult } from '../utils';

const PING_INTERVAL = 10000; // 10 seconds
const IDLE_WARNING_SECONDS = 540; // 9 minutes
const IDLE_TIMEOUT_SECONDS = 600; // 10 minutes
const REFRESH_FLAG_KEY = 'patientChartRefresh';
export const IDLE_WARNING_NOTIFICATION_KEY = 'idle-warning';

const startPatientChartSession = async ({
  patientGuid,
}: {
  patientGuid: string;
}) => {
  const { data, error } = await dataService.createOne(
    apiPatientChartSessionStart(),
    { patientGuid },
  );

  if (error) {
    showResult(error);
    throw new Error(error);
  }

  return data;
};

const pingPatientChartSession = async ({
  patientGuid,
  resetIdle,
  isRefresh,
}: {
  patientGuid: string;
  resetIdle?: boolean;
  isRefresh?: boolean;
}) => {
  const { data, error } = await dataService.createOne(
    apiPatientChartSessionPing(),
    {
      patientGuid,
      ...(resetIdle && { resetIdle: true }),
      ...(isRefresh && { isRefresh: true }),
    },
  );

  if (error) {
    console.error('Ping failed:', error);
    return null;
  }

  return data;
};

const sendEndSession = (sessionId: string | null, reasonEnded: string) => {
  if (sessionId) {
    navigator.sendBeacon(
      apiPatientChartSessionEnd(),
      JSON.stringify({ sessionId, reasonEnded }),
    );
  }
};

export const usePatientChartSession = ({
  patientGuid,
  canRun,
  onTimeout,
}: {
  patientGuid: string;
  canRun: boolean;
  onTimeout?: () => void;
}) => {
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [serverElapsedTime, setServerElapsedTime] = useState<number>(0);
  const [rpmTime, setRpmTime] = useState<number>(0);
  const [ccmTime, setCcmTime] = useState<number>(0);
  const [idleState, setIdleState] = useState<'warning' | 'timeout' | null>(
    null,
  );
  const [blocked, setBlocked] = useState(false);
  const [activePatientGuid, setActivePatientGuid] = useState<string | null>(
    null,
  );

  const pingIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const sessionRef = useRef<string | null>(null);
  const onTimeoutRef = useRef(onTimeout);
  const handlePingResultRef = useRef<((result: any) => void) | null>(null);
  const pendingActivityRef = useRef(false);
  const idleTimeoutRef = useRef(false);
  const blockedRef = useRef(false);
  const sessionEndedRef = useRef(false);

  useEffect(() => {
    onTimeoutRef.current = onTimeout;
  }, [onTimeout]);

  useEffect(() => {
    sessionRef.current = sessionId;
  }, [sessionId]);

  const handlePingResult = (result: any) => {
    if (!result?.sessionId) return;
    setSessionId(result.sessionId);
    setServerElapsedTime(result.elapsedTime);
    setRpmTime(result.rpmTime);
    setCcmTime(result.ccmTime);

    const idle = result.idleSeconds ?? 0;
    if (idle >= IDLE_TIMEOUT_SECONDS) {
      setIdleState('timeout');
      idleTimeoutRef.current = true;
      onTimeoutRef.current?.();
    } else if (idle >= IDLE_WARNING_SECONDS) {
      setIdleState('warning');
    } else {
      setIdleState(null);
    }
  };

  handlePingResultRef.current = handlePingResult;

  const startPingingRef = useRef<(() => void) | null>(null);

  const stopPinging = () => {
    if (pingIntervalRef.current) {
      clearInterval(pingIntervalRef.current);
      pingIntervalRef.current = null;
    }
  };

  const { mutate, isLoading, ...other } = useMutation({
    mutationFn: startPatientChartSession,
    onSuccess(data) {
      if (data.blocked) {
        blockedRef.current = true;
        setBlocked(true);
        setActivePatientGuid(data.activePatientGuid);
        return;
      }
      setSessionId(data.sessionId);
      setIdleState(null);
      pingPatientChartSession({ patientGuid }).then((r) =>
        handlePingResultRef.current?.(r),
      );
      startPingingRef.current?.();
    },
  });

  useEffect(() => {
    if (!patientGuid || !canRun) return;

    const startPinging = () => {
      if (pingIntervalRef.current) clearInterval(pingIntervalRef.current);
      pingIntervalRef.current = setInterval(async () => {
        const resetIdle = pendingActivityRef.current;
        pendingActivityRef.current = false;
        const result = await pingPatientChartSession({
          patientGuid,
          resetIdle,
        });
        handlePingResultRef.current?.(result);
      }, PING_INTERVAL);
    };

    startPingingRef.current = startPinging;

    let isPageHide = false;
    const handlePageHide = () => {
      isPageHide = true;
      if (blockedRef.current) return;
      if (sessionRef.current) {
        sessionStorage.setItem(REFRESH_FLAG_KEY, patientGuid);
      }
      sendEndSession(sessionRef.current, 'tab-close');
    };

    window.addEventListener('pagehide', handlePageHide);

    const refreshPatientGuid = sessionStorage.getItem(REFRESH_FLAG_KEY);
    const isRefresh = refreshPatientGuid === patientGuid;
    sessionStorage.removeItem(REFRESH_FLAG_KEY);

    if (isRefresh) {
      startPinging();
      pingPatientChartSession({ patientGuid, isRefresh: true }).then((r) =>
        handlePingResultRef.current?.(r),
      );
    } else {
      mutate({ patientGuid });
    }

    return () => {
      stopPinging();
      if (!isPageHide && !sessionEndedRef.current && idleTimeoutRef.current) {
        sendEndSession(sessionRef.current, 'idle-timeout');
      }
      sessionEndedRef.current = false;
      window.removeEventListener('pagehide', handlePageHide);
    };
  }, [patientGuid, canRun]);

  const endSession = (id: string | null) => {
    if (sessionEndedRef.current) return;
    sessionEndedRef.current = true;
    stopPinging();
    sendEndSession(id, 'chart-close');
  };

  return {
    isLoading,
    sessionId,
    serverElapsedTime,
    rpmTime,
    ccmTime,
    idleState,
    blocked,
    activePatientGuid,
    endSession,
    onActivity: () => {
      pendingActivityRef.current = true;
    },
    ...other,
  };
};
